import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/outline"
import { useState, type PropsWithChildren, type ReactNode } from "react"
import type {
  PartnershipBlastBlocker,
  PartnershipContentRowDto,
} from "~/ui-rtk/api/types/partnerships.types"
// Direct imports (not the `common` barrel) — see CreativesTab.tsx.
import Card from "~/ui-rtk/components/ui/common/Card"
import { Pagination } from "~/ui-rtk/components/ui/charts/TableVisualization/components/Pagination"
import Button from "~/ui-rtk/components/ui/controls/Button"
import Checkbox from "~/ui-rtk/components/ui/controls/Checkbox"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import {
  AD_ELIGIBILITY_LABEL,
  AD_USAGE_LABEL,
  BLOCKER_COPY,
  EFFECTIVE_RIGHTS_STATUS_LABEL,
  LAUNCH_BUTTON_LABEL,
  LAUNCH_UNAVAILABLE_TOOLTIP,
  NEEDS_AD_CODE_CHIP,
  PERMISSION_TYPE_LABEL,
  PLATFORM_LABEL,
  WARNING_COPY,
  formatEngagement,
  humanize,
} from "./partnership-copy"
import {
  AdCodeCell,
  NotesCell,
  RightsStatusSelect,
  WindowCell,
  type SaveContentFn,
  type SavePartnershipFn,
} from "./PartnershipRowEditors"

/* ----- Chips ----- */

export type ChipTone = "green" | "amber" | "red" | "grey" | "blue" | "purple"

const CHIP_TONE_CLS: Record<ChipTone, string> = {
  green: "bg-system-green-700 text-white",
  amber: "bg-system-yellow-700 text-white",
  red: "bg-system-red-700 text-white",
  grey: "bg-system-gray-700 text-system-gray-200",
  blue: "bg-system-blue-700 text-white",
  purple: "bg-system-purple-700 text-white",
}

interface StatusChipProps {
  tone: ChipTone
  title?: string
  className?: string
}
export function StatusChip({
  tone,
  title,
  className,
  children,
}: PropsWithChildren<StatusChipProps>) {
  return (
    <span
      title={title}
      className={cn(
        "inline-block whitespace-nowrap rounded-full px-system-sm py-system-2xs text-xs font-medium",
        CHIP_TONE_CLS[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

/** APPROVED green / PENDING amber / REVOKED red / anything else grey. */
function metaPermissionTone(status: string | null): ChipTone {
  switch ((status ?? "").toUpperCase()) {
    case "APPROVED":
      return "green"
    case "PENDING":
      return "amber"
    case "REVOKED":
      return "red"
    default:
      return "grey"
  }
}

function eligibilityTone(value: string | null): ChipTone {
  if (value === "ad_ready") return "green"
  if (value === "needs_attention") return "amber"
  return "grey"
}

/** ISO timestamp or 'YYYY-MM-DD' → the calendar day (UTC for timestamps). */
export function toDay(value: string | null): string | null {
  return value ? value.slice(0, 10) : null
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text
}

/* ----- Sorting ----- */

export type SortKey = "creator" | "posted" | "rights" | "window" | "ads"
export type SortDir = "asc" | "desc"
export interface SortState {
  key: SortKey
  dir: SortDir
}

export const DEFAULT_SORT_DIR: Record<SortKey, SortDir> = {
  creator: "asc",
  posted: "desc",
  rights: "asc",
  window: "asc",
  ads: "desc",
}

function sortValue(
  row: PartnershipContentRowDto,
  key: SortKey,
): string | number | null {
  switch (key) {
    case "creator":
      return (
        (
          row.partnership?.creatorHandle ?? row.authorDisplayName
        )?.toLowerCase() ?? null
      )
    case "posted":
      return row.postedAt ? Date.parse(row.postedAt) : null
    case "rights":
      return EFFECTIVE_RIGHTS_STATUS_LABEL[
        row.derived.effectiveRightsStatus
      ].toLowerCase()
    case "window":
      return row.derived.remainingDays
    case "ads":
      return row.derived.liveAdCount
  }
}

/** Rows without a value for the key always sink to the bottom (see CreativesTab). */
export function sortRows(
  rows: PartnershipContentRowDto[],
  sort: SortState | null,
): PartnershipContentRowDto[] {
  if (!sort) return rows
  const withValue: Array<{
    row: PartnershipContentRowDto
    v: string | number
  }> = []
  const without: PartnershipContentRowDto[] = []
  for (const row of rows) {
    const v = sortValue(row, sort.key)
    if (v === null) without.push(row)
    else withValue.push({ row, v })
  }
  const sign = sort.dir === "asc" ? 1 : -1
  withValue.sort((a, b) => {
    if (a.v < b.v) return -sign
    if (a.v > b.v) return sign
    return 0
  })
  return [...withValue.map(x => x.row), ...without]
}

const HEADER_CLS =
  "px-2 py-1.5 border-s border-s-basic-blue bg-basic-dark-blue text-md text-white font-bold text-left"

interface SortableHeaderProps {
  label: string
  sortKey: SortKey
  sort: SortState | null
  onToggle: (key: SortKey) => void
  className?: string
}
function SortableHeader({
  label,
  sortKey,
  sort,
  onToggle,
  className,
}: SortableHeaderProps) {
  const dir = sort?.key === sortKey ? sort.dir : null
  const SORT_ICON = { asc: ArrowUpIcon, desc: ArrowDownIcon } as const
  const Icon = dir ? SORT_ICON[dir] : ChevronUpDownIcon
  const ariaSort = { asc: "ascending", desc: "descending" } as const
  return (
    <th
      aria-sort={dir ? ariaSort[dir] : "none"}
      className={cn(HEADER_CLS, className)}
    >
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        aria-label={`Sort by ${label}`}
        title={`Sort by ${label}`}
        className={cn(
          "inline-flex items-center gap-1 whitespace-nowrap hover:text-brand",
          dir && "text-brand",
        )}
      >
        {label}
        <Icon className="h-3.5 w-3.5" />
      </button>
    </th>
  )
}

/* ----- Cells ----- */

const CELL_CLS = "px-2 py-1.5 border-s border-s-basic-blue text-sm align-top"

function CreatorCell({ row }: { row: PartnershipContentRowDto }) {
  const p = row.partnership
  if (!p) {
    return (
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate font-medium text-white">
          {row.authorDisplayName ?? "Unknown creator"}
        </span>
        <span className="text-xs text-gray-400">No creator linked</span>
      </div>
    )
  }
  const permission = p.metaPermissionStatus
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span
        className="truncate font-medium text-white"
        title={`@${p.creatorHandle}`}
      >
        @{p.creatorHandle}
      </span>
      {p.creatorDisplayName && (
        <span className="truncate text-xs text-gray-400">
          {p.creatorDisplayName}
        </span>
      )}
      <StatusChip
        tone={metaPermissionTone(permission)}
        className="self-start"
        title="Meta account-level ads permission"
      >
        {permission ? humanize(permission) : "Unknown"}
      </StatusChip>
    </div>
  )
}

interface ContentCellProps {
  row: PartnershipContentRowDto
  onSaveContent: SaveContentFn
  onDeleteContent: (row: PartnershipContentRowDto) => Promise<boolean>
}
function ContentCell({
  row,
  onSaveContent,
  onDeleteContent,
}: ContentCellProps) {
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const kind = row.postType ?? row.mediaType
  const notSeen = row.derived.warnings.includes("not_seen_in_latest_sync")
  const caption = row.caption?.trim()
  const engagement = formatEngagement(row.organicInsights)
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <div className="flex flex-wrap items-center gap-1 text-xs text-gray-400">
        <span>{PLATFORM_LABEL[row.platform]}</span>
        {kind && <span>· {humanize(kind)}</span>}
        {row.postedAt && <span>· {toDay(row.postedAt)}</span>}
        {row.source === "MANUAL" && (
          <StatusChip
            tone="purple"
            title="Entered by hand, not from Meta discovery"
          >
            Manual
          </StatusChip>
        )}
        {notSeen && (
          <StatusChip tone="amber" title={WARNING_COPY.not_seen_in_latest_sync}>
            Not in latest sync
          </StatusChip>
        )}
      </div>
      <span
        className="truncate text-sm text-white"
        title={caption || undefined}
      >
        {caption ? truncate(caption, 80) : "(no caption)"}
      </span>
      {engagement && (
        <span
          className="truncate text-xs text-gray-400"
          title={`Organic engagement on Instagram: ${engagement.full}`}
          data-testid="engagement"
        >
          {engagement.compact}
        </span>
      )}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        {row.permalink && (
          <a
            href={row.permalink}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-brand hover:underline"
          >
            Open post
          </a>
        )}
        <NotesCell row={row} onSaveContent={onSaveContent} />
        {row.source === "MANUAL" &&
          (confirmingDelete ? (
            <span className="inline-flex items-center gap-2 text-system-red-500">
              Delete this row?
              <button
                type="button"
                onClick={() => {
                  setConfirmingDelete(false)
                  void onDeleteContent(row)
                }}
                className="font-medium hover:underline"
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                className="text-gray-400 hover:text-white"
              >
                No
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="font-medium text-system-red-500 hover:underline"
            >
              Delete
            </button>
          ))}
      </div>
    </div>
  )
}

function MetaCell({ row }: { row: PartnershipContentRowDto }) {
  const eligibility = row.adEligibility
  const usage = row.adUsage
  const permissionType = row.permissionType
    ? (PERMISSION_TYPE_LABEL[row.permissionType] ??
      humanize(row.permissionType))
    : null
  return (
    <div className="flex flex-col items-start gap-1">
      <StatusChip
        tone={eligibilityTone(eligibility)}
        title={
          permissionType
            ? `Permission: ${permissionType}`
            : "No Meta discovery data for this post"
        }
      >
        {eligibility
          ? (AD_ELIGIBILITY_LABEL[eligibility] ?? humanize(eligibility))
          : "No Meta data"}
      </StatusChip>
      {usage && (
        <StatusChip tone={usage === "active" ? "blue" : "grey"}>
          {AD_USAGE_LABEL[usage] ?? humanize(usage)}
        </StatusChip>
      )}
    </div>
  )
}

function AdsCell({ row }: { row: PartnershipContentRowDto }) {
  const d = row.derived
  const names = row.ads
    .map(a => `${a.adName} (${a.effectiveStatus ?? "unknown"})`)
    .join("\n")
  return (
    <div className="flex flex-col items-start gap-1">
      <span
        className="whitespace-nowrap text-sm text-white"
        title={names || "No ads linked to this post yet"}
      >
        {d.liveAdCount}/{row.ads.length} live
      </span>
      {d.warnings.includes("live_ads_past_window") && (
        <StatusChip tone="red" title={WARNING_COPY.live_ads_past_window}>
          Live past window
        </StatusChip>
      )}
      {d.warnings.includes("armed_under_paused_parent") && (
        <StatusChip tone="amber" title={WARNING_COPY.armed_under_paused_parent}>
          Armed under paused parent
        </StatusChip>
      )}
    </div>
  )
}

/** True when the row's only blocker is the creator ad code — fixable from the Ad code column next door. */
export function isOnlyNeedsAdCode(
  blockers: PartnershipBlastBlocker[],
): boolean {
  return blockers.length === 1 && blockers[0] === "video_needs_ad_code"
}

interface LaunchCellProps {
  row: PartnershipContentRowDto
  /** `settings.blastAvailable` — the company-level gate (ad account, write access, brand identity). */
  launchAvailable: boolean
  onLaunch: (row: PartnershipContentRowDto) => void
}
/**
 * Launch column: the "Launch as ad" button when both the row and the
 * company can launch, otherwise why not. A row whose only blocker is the
 * creator ad code gets the button next to its chip: Meta can overrule that
 * blocker, so the preview's dry run decides. Every ad the button leads to is
 * created PAUSED — nothing in this column activates anything.
 */
function LaunchCell({ row, launchAvailable, onLaunch }: LaunchCellProps) {
  const { blockers, warnings, blastable } = row.derived
  const launchButton = (
    <Button
      size="xs"
      variant={{ variant: "solid", color: "blue" }}
      onClick={() => onLaunch(row)}
      title="Create a paused partnership ad from this post in ad sets you pick"
    >
      {LAUNCH_BUTTON_LABEL}
    </Button>
  )
  let state: ReactNode
  if (blastable && launchAvailable) {
    state = launchButton
  } else if (blastable) {
    state = (
      <StatusChip tone="grey" title={LAUNCH_UNAVAILABLE_TOOLTIP}>
        Blocked
      </StatusChip>
    )
  } else if (isOnlyNeedsAdCode(blockers)) {
    state = (
      <>
        {launchAvailable && launchButton}
        <StatusChip tone="amber" title={BLOCKER_COPY.video_needs_ad_code}>
          {NEEDS_AD_CODE_CHIP}
        </StatusChip>
      </>
    )
  } else {
    state = (
      <StatusChip
        tone="grey"
        title={blockers.map(b => BLOCKER_COPY[b]).join("\n")}
      >
        Blocked
      </StatusChip>
    )
  }
  return (
    <div className="flex flex-col items-start gap-1">
      {state}
      {warnings.length > 0 && (
        <span
          className="inline-flex items-center gap-1"
          role="img"
          aria-label={`${warnings.length} warning${warnings.length === 1 ? "" : "s"}`}
          title={warnings.map(w => WARNING_COPY[w]).join("\n")}
        >
          {warnings.map(w => (
            <span
              key={w}
              className="inline-block h-2 w-2 rounded-full bg-system-yellow-500"
            />
          ))}
        </span>
      )}
    </div>
  )
}

/* ----- Table ----- */

export interface TablePagination {
  pageIndex: number
  pageSize: number
  totalPages: number
  onNext: () => void
  onPrevious: () => void
  onChangeState: (
    partial: Partial<{ pageIndex: number; pageSize: number }>,
  ) => void
}

export interface PartnershipsTableProps {
  /** The current page of (already filtered + sorted) rows. */
  rows: PartnershipContentRowDto[]
  selected: Set<string>
  onToggleRow: (id: string) => void
  /** Toggle every row on this page. */
  onToggleAll: (ids: string[]) => void
  sort: SortState | null
  onToggleSort: (key: SortKey) => void
  onSavePartnership: SavePartnershipFn
  onSaveContent: SaveContentFn
  onDeleteContent: (row: PartnershipContentRowDto) => Promise<boolean>
  /** Rendered in the single empty row; may carry a button (e.g. "Show all"). */
  emptyStateCopy: ReactNode
  pagination: TablePagination | null
  /** `settings.blastAvailable`: whether this company can launch at all. */
  launchAvailable: boolean
  /** "Launch as ad" on a row: open the launch dialog for it. */
  onLaunch: (row: PartnershipContentRowDto) => void
}

export default function PartnershipsTable({
  rows,
  selected,
  onToggleRow,
  onToggleAll,
  sort,
  onToggleSort,
  onSavePartnership,
  onSaveContent,
  onDeleteContent,
  emptyStateCopy,
  pagination,
  launchAvailable,
  onLaunch,
}: PartnershipsTableProps) {
  const pageIds = rows.map(r => r.id)
  const allSelected =
    pageIds.length > 0 && pageIds.every(id => selected.has(id))
  const someSelected = selected.size > 0 && !allSelected

  return (
    <Card
      rounded
      className="border border-basic-blue bg-basic-dark-blue space-y-3"
    >
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="w-full border-b border-b-basic-blue">
            <tr>
              <th className="w-10 px-2 py-1.5 border-s-basic-blue bg-basic-dark-blue text-md text-white font-bold">
                <Checkbox
                  aria-label="Select all posts on this page"
                  checked={allSelected}
                  className={cn(someSelected && "opacity-60")}
                  onChange={() => onToggleAll(pageIds)}
                />
              </th>
              <SortableHeader
                label="Creator"
                sortKey="creator"
                sort={sort}
                onToggle={onToggleSort}
                className="w-40 min-w-40"
              />
              <SortableHeader
                label="Content"
                sortKey="posted"
                sort={sort}
                onToggle={onToggleSort}
                className="w-64 min-w-64 max-w-64"
              />
              <th className={cn(HEADER_CLS, "w-28")}>Meta</th>
              <SortableHeader
                label="Rights"
                sortKey="rights"
                sort={sort}
                onToggle={onToggleSort}
                className="w-52"
              />
              <SortableHeader
                label="Window"
                sortKey="window"
                sort={sort}
                onToggle={onToggleSort}
                className="w-44"
              />
              <th className={cn(HEADER_CLS, "w-28")}>Ad code</th>
              <SortableHeader
                label="Ads"
                sortKey="ads"
                sort={sort}
                onToggle={onToggleSort}
                className="w-28"
              />
              <th className={cn(HEADER_CLS, "w-28")}>Launch</th>
            </tr>
          </thead>
          <tbody className="w-full">
            {rows.map(row => {
              const isSelected = selected.has(row.id)
              return (
                <tr
                  key={row.id}
                  data-testid={`partnership-row-${row.id}`}
                  className={cn(
                    "border-b border-b-basic-blue bg-basic-black hover:bg-basic-dark-blue/70",
                    isSelected && "bg-basic-dark-blue/50",
                  )}
                >
                  <td className="px-2 py-1.5 text-sm align-top">
                    <Checkbox
                      aria-label={`Select ${row.partnership?.creatorHandle ?? row.id}`}
                      checked={isSelected}
                      onChange={() => onToggleRow(row.id)}
                    />
                  </td>
                  <td className={cn(CELL_CLS, "w-40 max-w-40 overflow-hidden")}>
                    <CreatorCell row={row} />
                  </td>
                  <td
                    className={cn(
                      CELL_CLS,
                      "w-64 min-w-64 max-w-64 overflow-hidden",
                    )}
                  >
                    <ContentCell
                      row={row}
                      onSaveContent={onSaveContent}
                      onDeleteContent={onDeleteContent}
                    />
                  </td>
                  <td className={CELL_CLS}>
                    <MetaCell row={row} />
                  </td>
                  <td className={CELL_CLS}>
                    <div className="flex flex-col items-start gap-1">
                      <RightsStatusSelect
                        row={row}
                        onSavePartnership={onSavePartnership}
                      />
                      {(row.derived.effectiveRightsStatus === "EXPIRED" ||
                        row.derived.effectiveRightsStatus === "REVOKED") && (
                        <StatusChip tone="red">
                          {
                            EFFECTIVE_RIGHTS_STATUS_LABEL[
                              row.derived.effectiveRightsStatus
                            ]
                          }
                        </StatusChip>
                      )}
                    </div>
                  </td>
                  <td className={CELL_CLS}>
                    <WindowCell
                      row={row}
                      onSavePartnership={onSavePartnership}
                      onSaveContent={onSaveContent}
                    />
                  </td>
                  <td className={CELL_CLS}>
                    <AdCodeCell row={row} onSaveContent={onSaveContent} />
                  </td>
                  <td className={CELL_CLS}>
                    <AdsCell row={row} />
                  </td>
                  <td className={CELL_CLS}>
                    <LaunchCell
                      row={row}
                      launchAvailable={launchAvailable}
                      onLaunch={onLaunch}
                    />
                  </td>
                </tr>
              )
            })}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  className="px-2 py-system-lg text-center text-sm text-gray-400 bg-basic-black"
                >
                  {emptyStateCopy}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {rows.length > 0 && pagination && (
        <div className="px-2 pb-2">
          <Pagination
            state={{
              pageIndex: pagination.pageIndex,
              pageSize: pagination.pageSize,
            }}
            total={pagination.totalPages}
            onNext={pagination.onNext}
            onPrevious={pagination.onPrevious}
            onChangeState={pagination.onChangeState}
          />
        </div>
      )}
    </Card>
  )
}
